import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authClient } from '../lib/auth-client';
import { AuthCard, Field, inputClass, primaryButtonClass, ErrorBanner } from '../components/AuthCard';
import { useAuthCapabilities } from '../hooks/useAuthCapabilities';
import { appendFlowFlag, googleSignInCallbackPath, resolvePostAuthPath } from '../lib/onboarding-flow';
import { postSignupPath } from '../lib/auth-flow-gates';

// Signup creates the USER only. There is deliberately no "personal org"
// created on their behalf — the next step (/onboarding/create-org)
// requires them to explicitly name their organization. This keeps the
// org concept honest: every org exists because a human named it.
export default function Signup() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const caps = useAuthCapabilities();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!caps) {
      setError('Still checking email verification settings. Try again in a moment.');
      return;
    }
    setSubmitting(true);
    const nextPath = resolvePostAuthPath(params.get('next'));
    const verificationTarget = appendFlowFlag(nextPath, 'verified');
    const callbackURL = typeof window !== 'undefined' ? `${window.location.origin}${verificationTarget}` : undefined;
    const { error: err } = await authClient.signUp.email({ email, password, name, callbackURL });
    setSubmitting(false);
    if (err) {
      setError(err.message || 'Could not create account.');
      return;
    }
    navigate(
      postSignupPath({
        email,
        nextPath: caps.emailVerificationRequired ? verificationTarget : nextPath,
        emailVerificationRequired: caps.emailVerificationRequired,
      }),
      { replace: true },
    );
  }

  async function onGoogleWorkspaceSignIn() {
    setError(null);
    setSubmitting(true);
    const nextPath = resolvePostAuthPath(params.get('next'));
    const callbackURL = typeof window !== 'undefined' ? googleSignInCallbackPath(window.location.origin, nextPath) : nextPath;
    const { error: err } = await authClient.signIn.social({ provider: 'google', callbackURL });
    setSubmitting(false);
    if (err) {
      setError(err.message || 'Google Workspace sign-in failed.');
    }
  }

  return (
    <AuthCard
      title="Create account"
      subtitle="Create your account first. You’ll name the organization workspace in the next step."
      footer={
        <>
          Already have an account?{' '}
          <Link to={`/login${params.get('next') ? `?next=${params.get('next')!}` : ''}`} className="font-semibold text-amber-700 hover:text-amber-800 hover:underline">
            Sign in
          </Link>
        </>
      }
    >
      <ErrorBanner message={error} />
      {caps?.googleWorkspaceSignInEnabled && (
        <>
          <button
            type="button"
            onClick={onGoogleWorkspaceSignIn}
            disabled={submitting}
            className="mb-3 flex w-full items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Continue with Google Workspace
          </button>
          <p className="mb-4 text-center text-xs text-slate-500">
            Google verifies your email automatically. Email/password uses an Oracord confirmation link.
          </p>
        </>
      )}
      <form onSubmit={onSubmit} className="space-y-4">
        <Field label="Your name">
          <input
            type="text"
            required
            autoComplete="name"
            autoFocus
            value={name}
            onChange={e => setName(e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label="Work email">
          <input
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label="Password">
          <input
            type="password"
            required
            autoComplete="new-password"
            minLength={10}
            value={password}
            onChange={e => setPassword(e.target.value)}
            className={inputClass}
          />
        </Field>
        <p className="rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-2 text-xs leading-5 text-slate-600">
          At least 10 characters. You’ll name your organization next.
        </p>
        <button type="submit" disabled={submitting || !caps} className={primaryButtonClass}>
          {!caps ? 'Checking email setup…' : submitting ? 'Creating account…' : 'Continue'}
        </button>
      </form>
    </AuthCard>
  );
}
